import { useMutation, UseMutationOptions, useQuery, useQueryClient } from "@tanstack/react-query"
import assetsApi from "../api/assets.api"
import { AssetType, UpdateAssetDataType } from "../types/bucketTypes"
import { AxiosResponse } from "axios"
import { useGlobalError } from "../contexts/global-error.context"
import { useTranslation } from "react-i18next"

export const useGetAssetAPI = (assetId?: number) => {
    if(!assetId) return;
    const { data, ...hookData } = useQuery({
        queryKey: ["getAsset", assetId],
        queryFn: (): Promise<AxiosResponse<{ data: AssetType }>> => assetsApi.getSingleAsset(assetId.toString()),
        staleTime: 1000 * 60 * 5, // 5 minutes
        gcTime: 1000 * 60 * 10, // 10 minutes
        placeholderData: (previousData) => previousData,
    })
    return { ...hookData, data: data?.data?.data }
}

export const useUpdateAssetAPI = (options?: UseMutationOptions<AxiosResponse, unknown, UpdateAssetDataType, unknown>) => {
    const { showSuccess } = useGlobalError();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    return useMutation(
        {
            ...options,
            mutationFn: assetsApi.updateAsset,
            onSuccess: (data, variables, onMutateResult, context) => {
                showSuccess(t("assetUpdatedSuccess"))
                options?.onSuccess?.(data, variables, onMutateResult, context);
                queryClient.invalidateQueries({ queryKey: ["getAsset", variables.asset_id] });
                try {
                    localStorage.setItem('selectedAsset', JSON.stringify(data?.data?.data));
                } catch (e) {
                    console.error('Failed to update selectedAsset in localStorage', e);
                }
            },
            onError: (error, variables, onMutateResult, context) => {
                options?.onError?.(error, variables, onMutateResult, context)
                console.error(error);
            },
        }
    )
}

export const useDeletePartAPI = (assetId: number, options?: UseMutationOptions<AxiosResponse, unknown, number, unknown>) => {
    const { showSuccess } = useGlobalError();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    return useMutation({
        ...options,
        mutationFn: (partId: number) => assetsApi.deleteReplacementPart(partId),
        onSuccess: (data, variables, onMutateResult, context) => {
            showSuccess(t("partDeletedSuccess"));
            options?.onSuccess?.(data, variables, onMutateResult, context);
            queryClient.invalidateQueries({ queryKey: ["getAsset", assetId] });
        },
        onError: (error, variables, onMutateResult, context) => {
            options?.onError?.(error, variables, onMutateResult, context);
            console.error(error);
        },
    })
}